import './KanbanBoard.css';
import React, { useState } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import type { DropResult } from 'react-beautiful-dnd';
import { Plus, MoreHorizontal, Clock, Tag, Archive, Trash2, Copy } from 'lucide-react';
import type { Task, Agent, TaskStatus } from '../types';
import { formatDistanceToNow } from 'date-fns';

interface KanbanBoardProps {
  tasks: Task[];
  agents: Agent[];
  onTaskMove: (taskId: string, newStatus: TaskStatus) => void;
  onTaskClick: (task: Task) => void;
  onCreateTask: (title: string, description: string) => void;
  onCreateMission: (title: string, description: string) => void;
  onArchiveTask: (taskId: string) => void;
  onDeleteTask: (taskId: string) => void;
}

const columns: { id: TaskStatus; title: string; color: string }[] = [
  { id: 'todo', title: 'TO DO', color: '#8B7355' },
  { id: 'inbox', title: 'TO DO', color: '#8B7355' },
  { id: 'blocked', title: 'Blocked', color: '#8B0000' },
  { id: 'in_progress', title: 'In Progress', color: '#CD853F' },
  { id: 'review', title: 'Review', color: '#B8860B' },
  { id: 'done', title: 'Done', color: '#228B22' },
];

const KanbanBoard: React.FC<KanbanBoardProps> = ({ 
  tasks, 
  agents, 
  onTaskMove, 
  onTaskClick,
  onCreateTask,
  onCreateMission,
  onArchiveTask,
  onDeleteTask
}) => {
  const [showNewTaskForm, setShowNewTaskForm] = useState(false);
  const [showNewMissionForm, setShowNewMissionForm] = useState(false);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [newTaskDesc, setNewTaskDesc] = useState('');
  const [expandedTaskId, setExpandedTaskId] = useState<string | null>(null);
  const [archivingTaskId, setArchivingTaskId] = useState<string | null>(null);
  const [copiedTaskId, setCopiedTaskId] = useState<string | null>(null);

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    
    const taskId = result.draggableId;
    const newStatus = result.destination.droppableId as TaskStatus;
    
    onTaskMove(taskId, newStatus);
  };

  const handleCreateTask = (e: React.FormEvent) => {
    e.preventDefault();
    if (newTaskTitle.trim()) {
      onCreateTask(newTaskTitle, newTaskDesc);
      setNewTaskTitle('');
      setNewTaskDesc('');
      setShowNewTaskForm(false);
    }
  };

  const handleCreateMission = (e: React.FormEvent) => {
    e.preventDefault();
    if (newTaskTitle.trim()) {
      onCreateMission(newTaskTitle, newTaskDesc);
      setNewTaskTitle('');
      setNewTaskDesc('');
      setShowNewMissionForm(false);
    }
  };

  const handleTaskClick = (task: Task, e: React.MouseEvent) => {
    // Don't expand if clicking archive button
    if ((e.target as HTMLElement).closest('.archive-btn')) {
      return;
    }
    
    if (expandedTaskId === task.id) {
      // If already expanded, open detail modal
      onTaskClick(task);
    } else {
      // Expand the task text
      setExpandedTaskId(task.id);
    }
  };

  const handleArchiveClick = (taskId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setArchivingTaskId(taskId);
    setTimeout(() => {
      onArchiveTask(taskId);
      setArchivingTaskId(null);
    }, 300);
  };

  const handleDeleteClick = (taskId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (confirm('Delete this task? This cannot be undone.')) {
      onDeleteTask(taskId);
    }
  };

  const handleCopyClick = async (task: Task, e: React.MouseEvent) => {
    e.stopPropagation();
    const text = `${task.title}\n\n${task.description || ''}`.trim();
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }
    setCopiedTaskId(task.id);
    setTimeout(() => setCopiedTaskId(null), 1500);
  };

  const getTasksByStatus = (status: TaskStatus) => 
    tasks.filter(t => t.status === status).sort((a, b) => 
      new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
    );

  const getAgentName = (id: string) => agents.find(a => a.id === id)?.name || 'Unknown';

  // Filter columns to only show unique ones (todo and inbox both map to TO DO)
  const uniqueColumns = columns.filter((col, index, self) => 
    index === self.findIndex(c => c.title === col.title)
  );

  return (
    <div className="kanban-container">
      {/* Side-by-side buttons */}
      <div className="kanban-actions">
        <button className="btn-primary" onClick={() => setShowNewMissionForm(true)}>
          <Plus size={16} />
          New Mission
        </button>
        <button className="btn-secondary" onClick={() => setShowNewTaskForm(true)}>
          <Plus size={16} />
          New Task
        </button>
      </div>

      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="kanban-board">
          {uniqueColumns.map(column => (
            <div key={column.id} className="kanban-column">
              <div className="column-header" style={{ borderColor: column.color }}>
                <h3>{column.title}</h3>
                <span className="task-count">
                  {getTasksByStatus(column.id).length}
                </span>
              </div>
              
              <Droppable droppableId={column.id}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={`column-content ${snapshot.isDraggingOver ? 'dragging-over' : ''}`}
                  >
                    {getTasksByStatus(column.id).map((task, index) => (
                      <Draggable key={task.id} draggableId={task.id} index={index}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            className={`task-card ${snapshot.isDragging ? 'dragging' : ''} ${expandedTaskId === task.id ? 'expanded' : ''} ${archivingTaskId === task.id ? 'archiving' : ''}`}
                            onClick={(e) => handleTaskClick(task, e)}
                            style={{
                              ...provided.draggableProps.style,
                            }}
                          >
                            <div className="task-priority-bar" data-priority={task.priority} />
                            
                            <h4 className="task-title">{task.title}</h4>
                            
                            {task.description && (
                              <p className={`task-preview ${expandedTaskId === task.id ? 'expanded' : ''}`}>
                                {expandedTaskId === task.id ? task.description : `${task.description.slice(0, 100)}${task.description.length > 100 ? '...' : ''}`}
                              </p>
                            )}
                            
                            <div className="task-meta">
                              <div className="task-assignees">
                                {task.assigneeIds.slice(0, 3).map(agentId => (
                                  <span key={agentId} className="assignee-avatar">
                                    {agents.find(a => a.id === agentId)?.emoji || '👤'}
                                  </span>
                                ))}
                                {task.assigneeIds.length > 3 && (
                                  <span className="assignee-more">+{task.assigneeIds.length - 3}</span>
                                )}
                              </div>
                              
                              <div className="task-tags">
                                {task.tags.slice(0, 2).map(tag => (
                                  <span key={tag} className="task-tag">
                                    <Tag size={10} />
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            </div>
                            
                            <div className="task-footer">
                              <span className="task-time">
                                <Clock size={12} />
                                {formatDistanceToNow(new Date(task.updatedAt), { addSuffix: true })}
                              </span>
                              {copiedTaskId === task.id && (
                                <span className="copy-indicator">Copied</span>
                              )}
                              <div className="task-actions">
                                {task.status === 'done' && (
                                  <button 
                                    className="archive-btn"
                                    onClick={(e) => handleArchiveClick(task.id, e)}
                                    title="Archive completed mission"
                                  >
                                    <Archive size={14} />
                                  </button>
                                )}
                                <button 
                                  className="copy-btn"
                                  onClick={(e) => handleCopyClick(task, e)}
                                  title="Copy task text"
                                >
                                  <Copy size={14} />
                                </button>
                                <button 
                                  className="delete-btn"
                                  onClick={(e) => handleDeleteClick(task.id, e)}
                                  title="Delete task"
                                >
                                  <Trash2 size={14} />
                                </button>
                                <button className="task-menu">
                                  <MoreHorizontal size={14} />
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </div>
          ))}
        </div>
      </DragDropContext>

      {/* New Task Modal */}
      {showNewTaskForm && (
        <div className="modal-overlay" onClick={() => setShowNewTaskForm(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3>Create New Task</h3>
            <form onSubmit={handleCreateTask}>
              <input
                type="text"
                placeholder="Task title..."
                value={newTaskTitle}
                onChange={e => setNewTaskTitle(e.target.value)}
                autoFocus
              />
              <textarea
                placeholder="Description (optional)..."
                value={newTaskDesc}
                onChange={e => setNewTaskDesc(e.target.value)}
                rows={3}
              />
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowNewTaskForm(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Create Task
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* New Mission Modal */}
      {showNewMissionForm && (
        <div className="modal-overlay" onClick={() => setShowNewMissionForm(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3>Create New Mission</h3>
            <form onSubmit={handleCreateMission}>
              <input
                type="text"
                placeholder="Mission title..."
                value={newTaskTitle}
                onChange={e => setNewTaskTitle(e.target.value)}
                autoFocus
              />
              <textarea
                placeholder="Mission description (optional)..."
                value={newTaskDesc}
                onChange={e => setNewTaskDesc(e.target.value)}
                rows={3}
              />
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowNewMissionForm(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Create Mission
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default KanbanBoard;
